/**
 * LIVE product repository — the marketing site's data seam. Pages/components consume ONLY this
 * module (never the raw API), exactly like the reference UI consumed its local-data repository, so
 * the swap to the backend needed no UI changes. Every field originates from the PUBLIC catalogue API
 * (`/public/products`, `/public/categories`, `/public/products/:id/details`, `/catalogues`) — the
 * BACKEND IS THE SINGLE SOURCE OF TRUTH. Nothing here is hardcoded product content.
 *
 * The catalogue is small and changes rarely, so products + categories are fetched once and cached
 * (with in-flight de-duplication); the reference UI's sync accessors read that cache. Per-product
 * detail (the admin-authored sections) is fetched on demand for the product page.
 */
import { fmtUom } from '../../lib/format';
import { slugify, productSlugCandidates } from '../lib/slug';
import { productAccent } from '../lib/accent';
import {
  fetchPublicProducts,
  fetchPublicCategories,
  fetchPublicDetails,
  fetchPublicCatalogues,
  type PublicProductDto,
  type PublicCategoryDto,
} from './publicApi';
import type { Category, CropSolution, DetailSection, Product, ProductForm, ProductResource, ProductVariant } from '../types';
import { cropSolutions } from '../data/crops';

export interface ProductFilters {
  query?: string;
  categories?: string[];
  applications?: string[];
  benefits?: string[];
  crops?: string[];
}
export interface FilterFacets {
  categories: Category[];
  applications: string[];
  benefits: string[];
  crops: string[];
}

// ---------------------------------------------------------------------------
// Cache (fetch-once). `load()` de-dupes concurrent callers via a shared promise.
// ---------------------------------------------------------------------------
interface Loaded {
  products: Product[];
  categories: Category[];
  bySlug: Map<string, Product>;
}
let _cache: Loaded | null = null;
let _inflight: Promise<Loaded> | null = null;
// Detail sections + resources for the WHOLE catalogue. Separate cache: the list endpoint does not
// carry them, so this is N detail calls, worth doing once for the pages that need all of them.
let _detailed: Product[] | null = null;
let _detailedInflight: Promise<Product[]> | null = null;

function masterKey(p: PublicProductDto): string {
  return (p.groupCode && p.groupCode.trim()) || p.sku;
}

function variantLabel(p: PublicProductDto): string {
  if (Number(p.packSize) > 0 && p.uomCode) {
    const s = fmtUom(p.packSize, p.uomCode);
    if (s) return s;
  }
  return p.variantLabel || p.uomCode || p.sku;
}

function toCategory(c: PublicCategoryDto): Category {
  return {
    slug: c.slug,
    name: c.name,
    shortName: c.shortName,
    tagline: c.tagline,
    description: c.description,
    accentColor: c.accent,
    icon: c.icon,
  };
}

/** Collapse the flat variant list into one UI Product per group. */
function buildProducts(rows: PublicProductDto[], categories: Category[]): Product[] {
  const catBySlug = new Map(categories.map((c) => [c.slug, c]));
  const groups = new Map<string, PublicProductDto[]>();
  for (const r of rows) {
    const k = masterKey(r);
    (groups.get(k) ?? groups.set(k, []).get(k)!).push(r);
  }

  const products: Product[] = [];
  const usedSlugs = new Set<string>();
  for (const [key, variantsRaw] of groups) {
    // Stable variant order: smallest pack first.
    const variantsRows = [...variantsRaw].sort((a, b) => Number(a.packSize) - Number(b.packSize));
    const rep = variantsRows[0];
    const name = rep.name;
    const categorySlug = rep.category ?? '';
    const category = categorySlug ? catBySlug.get(categorySlug) : undefined;

    let slug = slugify(name || key);
    if (!slug) slug = slugify(key);
    while (usedSlugs.has(slug)) slug = `${slug}-${slugify(key)}`;
    usedSlugs.add(slug);

    const variants: ProductVariant[] = variantsRows.map((v) => ({
      id: String(v.id),
      label: variantLabel(v),
      packSize: variantLabel(v),
      image: v.imageUrl ?? (v.imageUrls && v.imageUrls[0]) ?? null,
      sku: v.sku,
      mrp: Number(v.mrp) || undefined,
    }));

    const mrps = variantsRows.map((v) => Number(v.mrp)).filter((n) => n > 0);
    const purpose = (rep.summary ?? rep.description ?? '').trim();

    products.push({
      id: masterKey(rep),
      slug,
      name,
      categorySlug,
      accentColor: productAccent(slug, category?.accentColor),
      tagline: (rep.tagline ?? '').trim(),
      purpose,
      shortDescription: purpose,
      fullDescription: rep.description ? [rep.description.trim()] : [],
      benefits: rep.highlights ?? [], // the "Key highlights" field = the card ✓ bullets
      recommendedCrops: rep.crops ?? [],
      form: 'liquid' as ProductForm,
      variants,
      seo: {
        title: `${name}${rep.tagline ? ` — ${rep.tagline}` : ''} | GreenbayBio`,
        description: purpose || `${name} by Greenbay Biotech International.`,
      },
      applicationTags: rep.applicationTags ?? [],
      benefitTags: rep.benefitTags ?? [],
      mrpFrom: mrps.length ? Math.min(...mrps) : undefined,
    });
  }
  products.sort((a, b) => a.name.localeCompare(b.name));
  return products;
}

async function load(): Promise<Loaded> {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = (async () => {
    try {
      const [rows, cats] = await Promise.all([fetchPublicProducts(), fetchPublicCategories()]);
      const categories = cats.map(toCategory);
      const products = buildProducts(rows, categories);
      const bySlug = new Map(products.map((p) => [p.slug, p]));
      _cache = { products, categories, bySlug };
      return _cache;
    } finally {
      // Always clear the in-flight slot — even on a rejected fetch — so a transient network/5xx
      // error doesn't wedge the whole catalogue for the rest of the session (the next call retries).
      _inflight = null;
    }
  })();
  return _inflight;
}

/**
 * Resolve a requested slug to a product, tolerantly. In order: the catalogue's own slug, the alias
 * table (an editorial or previously-indexed slug for a product that has since been renamed), the
 * master key / group code, then any variant SKU. Case-insensitive throughout, so an old
 * `/catalog/AGN` link still lands on the AGN page — which then declares the canonical url itself.
 */
function resolve(loaded: Loaded, slug: string): Product | undefined {
  const want = String(slug ?? '').trim();
  if (!want) return undefined;
  const candidates = productSlugCandidates(want);
  for (const c of candidates) {
    const hit = loaded.bySlug.get(c);
    if (hit) return hit;
  }
  const raw = want.toLowerCase();
  return loaded.products.find(
    (p) =>
      candidates.includes(p.slug.toLowerCase()) ||
      candidates.includes(slugify(p.id)) ||
      p.id.toLowerCase() === raw ||
      p.variants.some((v) => v.sku?.toLowerCase() === raw),
  );
}

/** Invalidate the cache (e.g. for a manual refresh). */
export function clearProductCache() {
  _cache = null;
  _inflight = null;
  _detailed = null;
  _detailedInflight = null;
}

// ---------------------------------------------------------------------------
// Detail-section adapter — maps the admin-authored sections to the detail view.
// Everything the dashboard authored is preserved (nothing dropped): a first
// "description" TEXT becomes the intro paragraphs; the rest render generically
// on the product page via `extraSections`, in authored order.
// ---------------------------------------------------------------------------
const DESC_RE = /description|about|overview|what is/i;

function splitParas(text?: string): string[] {
  return (text ?? '')
    .split(/\n{2,}|\r\n\r\n/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function mapDetail(base: Product, sections: DetailSection[], resources: ProductResource[], catalogues: Record<string, string>): Product {
  const extra: DetailSection[] = [];
  let fullDescription = base.fullDescription;
  let tookDescription = false;

  for (const s of sections ?? []) {
    if (!tookDescription && s.kind === 'TEXT' && DESC_RE.test(s.title || '') && (s.text ?? '').trim()) {
      fullDescription = splitParas(s.text);
      tookDescription = true;
      continue; // promoted to the intro; don't also render generically
    }
    extra.push(s);
  }

  const cats: ProductResource[] = Object.entries(catalogues || {}).map(([lang, url]) => ({
    label: 'Catalogue',
    url,
    lang,
  }));

  return {
    ...base,
    fullDescription: fullDescription.length ? fullDescription : base.fullDescription,
    extraSections: extra,
    resources: [...(resources ?? []), ...cats],
  };
}

// ---------------------------------------------------------------------------
// Filtering / facets (client-side, over the cached list).
// ---------------------------------------------------------------------------
function matchesQuery(p: Product, q: string): boolean {
  const hay = [p.name, p.tagline, p.purpose, p.shortDescription, p.categorySlug, ...(p.recommendedCrops ?? []), ...p.benefits]
    .join(' ')
    .toLowerCase();
  return q
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((t) => hay.includes(t));
}

function applyFilters(list: Product[], f: ProductFilters): Product[] {
  return list.filter((p) => {
    if (f.query && f.query.trim() && !matchesQuery(p, f.query.trim())) return false;
    if (f.categories?.length && !f.categories.includes(p.categorySlug)) return false;
    if (f.crops?.length) {
      const crops = (p.recommendedCrops ?? []).map((c) => c.toLowerCase());
      if (!f.crops.some((c) => crops.some((pc) => pc.includes(c.toLowerCase())))) return false;
    }
    if (f.benefits?.length && !f.benefits.some((b) => p.benefitTags.includes(b))) return false;
    if (f.applications?.length && !f.applications.some((a) => p.applicationTags.includes(a))) return false;
    return true;
  });
}

// ---------------------------------------------------------------------------
// Public API (mirrors the reference repository so ported pages need no changes).
// ---------------------------------------------------------------------------
export const productRepository = {
  async getAllProducts(): Promise<Product[]> {
    return (await load()).products;
  },
  getAllProductsSync(): Product[] {
    return _cache?.products ?? [];
  },

  async getProductBySlug(slug: string): Promise<Product | undefined> {
    const loaded = await load();
    const base = resolve(loaded, slug);
    if (!base) return undefined;
    // The rich-detail + catalogue endpoints resolve by a NUMERIC product id (findById → the group), NOT
    // the group-code string that is `base.id`. Use any variant's real row id so the sections actually load.
    const detailId = base.variants[0]?.id ?? base.id;
    const [details, catalogues] = await Promise.all([
      fetchPublicDetails(detailId).catch(() => null),
      fetchPublicCatalogues(detailId),
    ]);
    if (!details) return mapDetail(base, [], [], catalogues);
    return mapDetail(base, details.sections ?? [], details.resources ?? [], catalogues);
  },
  getProductBySlugSync(slug: string): Product | undefined {
    return _cache ? resolve(_cache, slug) : undefined;
  },

  async getFeaturedProducts(limit = 6): Promise<Product[]> {
    const products = (await load()).products;
    // No `featured` flag in the backend yet — derive: one representative per category, then fill.
    const seen = new Set<string>();
    const picked: Product[] = [];
    for (const p of products) {
      if (p.categorySlug && !seen.has(p.categorySlug)) {
        seen.add(p.categorySlug);
        picked.push(p);
      }
    }
    for (const p of products) if (picked.length < limit && !picked.includes(p)) picked.push(p);
    return picked.slice(0, limit);
  },

  async getProductsByCategory(categorySlug: string): Promise<Product[]> {
    return (await load()).products.filter((p) => p.categorySlug === categorySlug);
  },

  /**
   * Every product WITH its admin-authored detail sections and documents.
   *
   * The list endpoint returns card-level fields only, so the document library, the approvals register
   * and the machine-readable exports would each have had to fetch details product by product. Doing it
   * once here, cached, keeps them reading the same records the product pages read. A product whose
   * detail call fails keeps its card-level fields rather than disappearing from the list.
   */
  async getAllProductsDetailed(): Promise<Product[]> {
    if (_detailed) return _detailed;
    if (_detailedInflight) return _detailedInflight;
    _detailedInflight = (async () => {
      try {
        const { products } = await load();
        const full = await Promise.all(
          products.map(async (p) => (await productRepository.getProductBySlug(p.slug)) ?? p),
        );
        _detailed = full;
        return full;
      } finally {
        _detailedInflight = null;
      }
    })();
    return _detailedInflight;
  },

  getRelatedProducts(product: Product, limit = 3): Product[] {
    const all = _cache?.products ?? [];
    return all.filter((p) => p.categorySlug === product.categorySlug && p.slug !== product.slug).slice(0, limit);
  },

  async searchProducts(filters: ProductFilters): Promise<Product[]> {
    return applyFilters((await load()).products, filters);
  },
  filterSync(filters: ProductFilters): Product[] {
    return applyFilters(_cache?.products ?? [], filters);
  },

  async getCategories(): Promise<Category[]> {
    return (await load()).categories;
  },
  getCategoriesSync(): Category[] {
    return _cache?.categories ?? [];
  },
  async getCategoryBySlug(slug: string): Promise<Category | undefined> {
    return (await load()).categories.find((c) => c.slug === slug);
  },

  async getCropSolutions(): Promise<CropSolution[]> {
    // Curated editorial crop programmes (data/crops.ts) — grouping, intros and programme notes are
    // hand-authored from the product sheets (like faq.ts/company.ts). Product name/image/price/detail
    // stay 100% live: `await load()` warms the product cache so the pages' getProductBySlugSync resolves
    // real cards. recommendedCrops still powers the catalogue's Crop filter facet, separately.
    await load();
    return cropSolutions;
  },
  async getCropBySlug(slug: string): Promise<CropSolution | undefined> {
    await load();
    return cropSolutions.find((c) => c.slug === slug);
  },

  async getFilterFacets(): Promise<FilterFacets> {
    const { products, categories } = await load();
    const crops = new Set<string>();
    const applications = new Set<string>();
    const benefits = new Set<string>();
    for (const p of products) {
      (p.recommendedCrops ?? []).forEach((c) => crops.add(c));
      p.applicationTags.forEach((a) => applications.add(a));
      p.benefitTags.forEach((b) => benefits.add(b));
    }
    return {
      categories,
      applications: [...applications].sort(),
      benefits: [...benefits].sort(),
      crops: [...crops].sort(),
    };
  },
};

export type ProductRepository = typeof productRepository;
